import { prepareQueryParams } from '@homeviews/utils'
import { api, API_V2_BASE_URL } from '@homeviews/store'
import type {
  PageViewStatistics,
  AreaGuideBenchmark,
} from '@homeviews/analytics'

type PageViewQueryParams = {
  // Gets PageViews only for authenticated user's Developer
  portalDeveloper: boolean
  dateFrom: string // e.g. '2021-11-03'
  dateTo: string // e.g. '2022-11-03'
}

// only filters used for specific graphs
const optionalFilters = ['rating_breakdown_category']

const settings = {
  optionalFilters,
}

export const statisticsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getSummaryStats: builder.query({
      query: (params) => {
        return {
          url: `/company-summary-stats${prepareQueryParams(params, settings, [
            'rating_breakdown_category',
          ])}`,
        }
      },
    }),
    getDevelopmentsStats: builder.query({
      query: (params) => {
        return {
          url: `/developments-stats${prepareQueryParams(params, settings)}`,
        }
      },
    }),
    getReviewsStats: builder.query({
      query: (params) => {
        return {
          url: `/reviews-stats${prepareQueryParams(params, settings)}`,
        }
      },
    }),
    getDevelopmentsAverage: builder.query({
      query: (params) => {
        return {
          url: `/developments-average${prepareQueryParams(
            params,
            settings,
            [],
            ['development[]'],
          )}`,
        }
      },
    }),
    getDevelopmentsBenchmark: builder.query({
      query: (params) => {
        return {
          url: `/developments-benchmark${prepareQueryParams(params, settings)}`,
        }
      },
    }),
    getReviewerTypesBenchmark: builder.query({
      query: (params) => {
        return {
          url: `/reviews-rating-benchmark${prepareQueryParams(
            params,
            settings,
          )}`,
        }
      },
    }),
    getAwardsStats: builder.query({
      query: (params) => {
        return {
          url: `/awards${prepareQueryParams(params, settings)}`,
        }
      },
    }),
    getAreaRanksStats: builder.query({
      query: () => {
        return {
          url: '/area-ranks',
        }
      },
    }),
    getReviewsCountHistory: builder.query({
      query: (params) => {
        return {
          url: `/reviews-count-over-time${prepareQueryParams(
            params,
            settings,
          )}`,
        }
      },
    }),
    getOverallRatingHistory: builder.query({
      query: (params) => {
        return {
          url: `/reviews-overall-rating-over-time${prepareQueryParams(params)}`,
        }
      },
    }),
    getReviewsRatingHistory: builder.query({
      query: (params) => {
        return {
          url: `/reviews-rating-over-time${prepareQueryParams(
            params,
            settings,
          )}`,
        }
      },
    }),
    getDashboardDevelopers: builder.query({
      query: (params) => {
        return {
          url: `/dashboard-developers${prepareQueryParams(params, settings)}`,
        }
      },
    }),
    getDashboardDevelopments: builder.query({
      query: (params) => {
        return {
          url: `/dashboard-developments${prepareQueryParams(params, settings)}`,
        }
      },
    }),
    getAdditionalQuestionsBenchmark: builder.query({
      query: (params) => {
        return {
          url: `/reviews-additional-questions-benchmark${prepareQueryParams(
            params,
          )}`,
        }
      },
    }),
    getOwnerReviews: builder.query({
      query: (params) => {
        return {
          url: `/reviews-by-owner-types${prepareQueryParams(params, settings)}`,
        }
      },
    }),
    getPageViewStatistics: builder.query<
      PageViewStatistics,
      PageViewQueryParams
    >({
      query: (params) => ({
        url: `${API_V2_BASE_URL}/page-view-statistics${prepareQueryParams(
          params,
        )}`,
      }),
    }),
    getAreaGuideBenchmark: builder.query<AreaGuideBenchmark, null>({
      query: () => ({
        url: '/area-guide-benchmark',
      }),
    }),
  }),
})

export const {
  useGetSummaryStatsQuery,
  useGetDevelopmentsStatsQuery,
  useGetDashboardDevelopersQuery,
  useGetDashboardDevelopmentsQuery,
  useGetReviewsStatsQuery,
  useGetAreaRanksStatsQuery,
  useGetAwardsStatsQuery,
  useGetDevelopmentsAverageQuery,
  useGetDevelopmentsBenchmarkQuery,
  useGetReviewsCountHistoryQuery,
  useGetReviewsRatingHistoryQuery,
  useGetReviewerTypesBenchmarkQuery,
  useGetAdditionalQuestionsBenchmarkQuery,
  useGetOwnerReviewsQuery,
  useGetOverallRatingHistoryQuery,
  useGetPageViewStatisticsQuery,
  useGetAreaGuideBenchmarkQuery,
} = statisticsApi
